import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'sonner';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { GOAL_METRIC_OPTIONS } from '../../types/analytics';
import type { Goal, GoalProgressEntry } from '../../types/analytics';
import GoalForm from './GoalForm';

const formatNumber = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
};

const isLowerBetter = (metricName: string | null): boolean => {
  if (!metricName) return false;
  const opt = GOAL_METRIC_OPTIONS.find((o) => o.value === metricName);
  return Boolean(opt?.lowerIsBetter);
};

const ratioForBar = (
  current: number | null,
  target: number | null,
  lowerBetter: boolean
): number => {
  if (current === null || target === null || target === 0) return 0;
  if (lowerBetter) {
    // Below target → 100%, above target → degrades toward 0%.
    if (current <= target) return 100;
    const overshoot = (current - target) / target;
    return Math.max(0, Math.min(100, (1 - overshoot) * 100));
  }
  return Math.max(0, Math.min(100, (current / target) * 100));
};

const GoalsManager: React.FC = () => {
  const { goals, goalsLoading, insights, loadGoals, deleteGoal } = useAnalyticsStore();
  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  useEffect(() => {
    loadGoals().catch(() => {
      /* error already logged in store */
    });
  }, [loadGoals]);

  // Match progress entries by goal_id only. Name-based fallback was unsafe
  // because `goal_name` is not unique — two goals with the same name would
  // silently show the wrong progress.
  const progressByGoalId = useMemo(() => {
    const map = new Map<string, GoalProgressEntry>();
    for (const entry of insights?.goal_progress ?? []) {
      if (entry.goal_id) map.set(entry.goal_id, entry);
    }
    return map;
  }, [insights]);

  const findProgress = (goal: Goal): GoalProgressEntry | undefined => progressByGoalId.get(goal.id);

  const openCreate = () => {
    setEditingGoal(null);
    setFormOpen(true);
  };

  const openEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setFormOpen(true);
  };

  const handleDelete = async (goal: Goal) => {
    if (!window.confirm(`Delete goal "${goal.name}"?`)) return;
    try {
      await deleteGoal(goal.id);
      toast.success('Goal deleted');
    } catch (err) {
      console.error('Failed to delete goal:', err);
      toast.error('Failed to delete goal');
    }
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Goals & KPIs
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={openCreate}
          size="small"
          variant="outlined"
          sx={{ ml: 'auto' }}
          data-testid="goal-add-btn"
        >
          New goal
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Numeric and qualitative goals that anchor the weekly insights generator. Numeric progress is
        read from the most recent insights snapshot.
      </Typography>

      {goalsLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={42} />
          ))}
        </Box>
      ) : goals.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          No goals yet. Add one to anchor next week's insights.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Current / Progress</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Target date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {goals.map((goal) => {
                const progress = findProgress(goal);
                const lowerBetter = isLowerBetter(goal.metric_name);
                const bar = ratioForBar(
                  progress?.current_value ?? null,
                  goal.target_value,
                  lowerBetter
                );
                return (
                  <TableRow key={goal.id} data-testid={`goal-row-${goal.id}`}>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                        {goal.name}
                      </Typography>
                      {goal.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontSize: 11 }}
                        >
                          {goal.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={goal.goal_type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10 }}
                      />
                      {lowerBetter && (
                        <Chip
                          label="lower is better"
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: 9, ml: 0.5, height: 18 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatNumber(goal.target_value)}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      {goal.goal_type === 'numeric' ? (
                        <>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: 11 }}>
                            {formatNumber(progress?.current_value ?? null)}
                            {progress?.current_value !== null &&
                              progress?.current_value !== undefined &&
                              goal.target_value !== null && (
                                <> / {formatNumber(goal.target_value)}</>
                              )}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={bar}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {progress?.evidence ?? '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {goal.target_date ?? <Typography color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={goal.status}
                        size="small"
                        color={
                          goal.status === 'achieved'
                            ? 'success'
                            : goal.status === 'abandoned'
                              ? 'default'
                              : 'primary'
                        }
                        sx={{ fontSize: 10 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(goal)}
                        data-testid={`goal-edit-${goal.id}`}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(goal)}
                        data-testid={`goal-delete-${goal.id}`}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GoalForm open={formOpen} goal={editingGoal} onClose={() => setFormOpen(false)} />
    </Paper>
  );
};

export default GoalsManager;
